import { Alert, Box, Button, Typography } from "@mui/material";
import { useState } from "react";

import api from "services/api";
import { BuscaPessoa, IPedidoPorMotivo } from "v2/components/BuscaPessoa";

import { AcontecimentosBloco } from "./AcontecimentosBloco";
import { BotaoComMenu } from "./BotaoComMenu";
import { Numero } from "./Numero";
import { SeloVisibilidade, TituloComSelo } from "../AbasRh/Visibilidade";
import { SeloVisibilidadeAba } from "../AbasRh/types";
import { IRhHoje } from "./types";

export async function getRhHoje(): Promise<IRhHoje> {
  const { data } = await api.get<IRhHoje>("/insights/rh/hoje");
  return data;
}

/**
 * A aba só aparece quando há o que mostrar. 401 (o ensureAuthenticated no modo
 * APLICAR) e 403 da rota (não é comprador, ou sessão sem organização ou de outra
 * empresa) escondem, sem alerta; erro de rede mostra a aba com a mensagem, para
 * não virar tela branca nem sumir em silêncio. Enquanto carrega, a aba não existe.
 */
export function mostrarHoje(q: { data?: IRhHoje; error: unknown }): boolean {
  if (q.error) {
    const status = (q.error as { response?: { status?: number } }).response?.status;
    return status !== 401 && status !== 403;
  }
  return q.data?.estado === "OK";
}

interface IHojeTabProps {
  dados?: IRhHoje;
  erro: boolean;
  selo?: SeloVisibilidadeAba | null;
}

/** Aba Hoje do comprador (spec do RH §6). Todo texto vem pronto do backend. */
export function HojeTab({ dados, erro, selo }: IHojeTabProps) {
  // Linha de "Fora do caminho comum" clicada: abre a busca já filtrada pelo motivo (F2).
  const [pedido, setPedido] = useState<IPedidoPorMotivo | null>(null);

  if (erro || !dados) {
    return (
      <Alert severity="error">Não foi possível carregar a aba Hoje. Tente de novo em alguns minutos.</Alert>
    );
  }

  const { blocos } = dados;
  const alerta = blocos?.maxima.some((l) => l.alerta) ?? false;

  return (
    <Box display="flex" flexDirection="column" gap={3} maxWidth={1100}>
      <TituloComSelo titulo={dados.veredito} aoLado={<SeloVisibilidade selo={selo} />} />

      {dados.avisos.map((aviso) => (
        <Alert key={aviso} severity="info">
          {aviso}
        </Alert>
      ))}

      {blocos && (
        <>
          {blocos.proximoVencimento && <Typography variant="body1">{blocos.proximoVencimento}</Typography>}

          <Box display="grid" gap={2} gridTemplateColumns="repeat(auto-fit, minmax(240px, 1fr))">
            <Numero
              numero={blocos.numeros.prontas}
              acao={<BotaoComMenu rotulo="Aprovar" contratos={dados.aprovar} variante="contained" />}
            />
            <Numero numero={blocos.numeros.emAndamento} />
            <Numero numero={blocos.numeros.concluidasNoMes} />
          </Box>

          <Box
            data-testid="faixa-maxima"
            data-alerta={String(alerta)}
            sx={{
              p: 2,
              borderRadius: 1,
              bgcolor: alerta ? "error.main" : "grey.100",
              color: alerta ? "error.contrastText" : "text.primary",
            }}
          >
            {blocos.maxima.map((linha) => (
              <Typography key={linha.texto} fontWeight={linha.alerta ? 700 : 400}>
                {linha.texto}
              </Typography>
            ))}
          </Box>

          <Box display="flex" flexDirection="column" gap={1}>
            <Typography component="h3" variant="h6" sx={{ fontFamily: "Sora" }}>
              {blocos.foraDoCaminho.titulo}
            </Typography>
            {blocos.foraDoCaminho.vazio && (
              <Typography color="text.secondary">{blocos.foraDoCaminho.vazio}</Typography>
            )}
            {blocos.foraDoCaminho.itens.map((item) => (
              <Box key={item.rotulo} display="flex" gap={1} alignItems="baseline">
                <Button
                  variant="text"
                  sx={{ p: 0, minWidth: 0, textTransform: "none", fontSize: "1rem", fontWeight: 400 }}
                  onClick={() =>
                    setPedido({ motivo: item.motivo, rotulo: item.rotulo, seq: (pedido?.seq ?? 0) + 1 })
                  }
                >
                  {item.rotulo}: {item.n.toLocaleString("pt-BR")}
                </Button>
                {item.novos && (
                  <Typography fontWeight={700} color="primary.main">
                    {item.novos}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>

          {dados.acontecimentos && <AcontecimentosBloco dados={dados.acontecimentos} />}

          {/* F3 (contrato §7): "Seus lotes recentes" entra aqui, lendo `lotes` da mesma resposta. */}
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography component="h3" variant="h6" sx={{ fontFamily: "Sora" }}>
              Onde está esta pessoa?
            </Typography>
            <BuscaPessoa pedido={pedido} />
          </Box>
        </>
      )}
    </Box>
  );
}
